import { useMutation, useQuery } from '@tanstack/react-query';
import { useDispatch, useSelector } from 'react-redux';
import { selectAccount, selectAuthToken } from 'src/store/selectors';
import {
  generateVerificationCode,
  getResearcherOrganizations,
  getUserById,
  getUserOverview,
  updateUser,
  UpdateUserDTO,
  verifyUserAttribute,
} from './users';
import { User, UserOverview } from 'src/types/users';
import { configuration } from 'src/configuration';
import { useTranslation } from 'react-i18next';
import { showToast } from 'src/store/toast';
import { OrganizationResearcher } from 'src/types/organizations';

export function useGetUserById(userId: string) {
  const token = useSelector(selectAuthToken);

  return useQuery<User>({
    queryKey: ['user', userId],
    queryFn: async () => {
      return await getUserById({ token, id: userId });
    },
    staleTime: configuration.queriesStaleTime,
  });
}

export function useGetUserOverview() {
  const token = useSelector(selectAuthToken);
  const account = useSelector(selectAccount);

  return useQuery<UserOverview>({
    queryKey: ['user-overview'],
    queryFn: async () => {
      return await getUserOverview({ token, userId: account.userId });
    },
    staleTime: configuration.queriesStaleTime,
  });
}

export function useGetResearcherOrganizations() {
  const token = useSelector(selectAuthToken);
  const account = useSelector(selectAccount);

  return useQuery<{
    organizations: OrganizationResearcher[];
  }>({
    queryKey: ['researcher-organizations'],
    queryFn: async () => {
      return await getResearcherOrganizations({ token, researcherId: account.researcherId });
    },
    staleTime: configuration.queriesStaleTime,
  });
}

export function useUpdateUserMutation({ showConfirmationToast = true }) {
  const token = useSelector(selectAuthToken);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { userId } = useSelector(selectAccount);
  const getUserByIdQuery = useGetUserById(userId);

  return useMutation({
    mutationFn: (updatedUser: UpdateUserDTO) => {
      return updateUser({ token, updatedUser });
    },
    onSuccess: () => {
      if (showConfirmationToast) {
        dispatch(
          showToast({
            type: 'success',
            message: t('success_profile_updated'),
          }),
        );
      }
      getUserByIdQuery.refetch();
    },
  });
}

export function useGenerateVerificationCodeMutation(
  verificationAttribute: 'email' | 'phone_number',
) {
  const token = useSelector(selectAuthToken);
  const { accessToken } = useSelector(selectAccount);
  const dispatch = useDispatch();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: () => {
      return generateVerificationCode({ token, accessToken, attributeName: verificationAttribute });
    },
    onSuccess: () => {
      dispatch(
        showToast({
          type: 'success',
          message: t('verification_sent', {
            attribute: verificationAttribute === 'email' ? t('email') : t('phone_number'),
          }),
        }),
      );
    },
  });
}

export function useVerifyUserAttribute(verificationAttribute: 'email' | 'phone_number') {
  const token = useSelector(selectAuthToken);
  const { accessToken } = useSelector(selectAccount);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { userId } = useSelector(selectAccount);
  const getUserByIdQuery = useGetUserById(userId);

  return useMutation({
    mutationFn: ({ code }: { code: string }) => {
      return verifyUserAttribute({
        token,
        attributeName: verificationAttribute,
        code,
        accessToken,
      });
    },
    onSuccess: () => {
      dispatch(
        showToast({
          type: 'success',
          message: t('email_phone_verified', {
            attribute: verificationAttribute === 'email' ? t('email') : t('phone_number'),
          }),
        }),
      );
      getUserByIdQuery.refetch();
    },
  });
}
